/**
 * components/ErrorBoundary.tsx — top-of-tree React error boundary.
 *
 * Why this exists
 * ---------------
 * Without an error boundary, ANY React runtime error inside the tree
 * (a Rules-of-Hooks violation, a `.map()` on undefined, a bad ref
 * unwrap) crashes React's reconciler and leaves `<div id="root">`
 * empty. On a dark-theme build, the empty root shows through as
 * `body { background: var(--canvas) }` — a solid black rectangle
 * with no way to escape except a hard refresh.
 *
 * The specific incident this closed
 * ---------------------------------
 * SignInOtp.tsx used to declare four hooks BEFORE two early-return
 * `<Navigate>` branches and eight hooks BELOW them. When
 * `refresh()` completed after a successful OTP verify, AuthContext
 * flipped `status` to `"signed_in"` and the next render hit the
 * top early-return — dropping the effective hook count from ~18
 * to ~10. React's reconciler surfaced this as "Rendered fewer
 * hooks than expected"; without a boundary, the whole app went
 * to black.
 *
 * With this boundary
 * ------------------
 *   * A runtime error caught here renders a minimal, tokens-only
 *     recovery card WITH VISIBLE COPY. The user knows something
 *     broke and can reload without opening DevTools.
 *   * `componentDidCatch` funnels the error + component stack to
 *     the console AND (best-effort) `POST /api/v1/log/client-error`
 *     so we can trace which route broke. The POST is fire-and-
 *     forget; failure to log does NOT block the UI.
 *   * Only ONE boundary at the app root. Sub-boundaries per route
 *     are noise until a specific route needs isolation — YAGNI.
 *
 * Scope discipline
 * ----------------
 * This is NOT an anti-pattern crutch for hook bugs. Hook bugs are
 * still bugs and should be fixed at source (see SignInOtp.tsx's
 * HOOKS BLOCK comment). This is defense in depth for the class of
 * error that turns "one broken route" into "the whole SPA is
 * unusable" — the black-screen incident.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import styles from "./ErrorBoundary.module.css";


interface Props {
  children: ReactNode;
}

interface State {
  err: Error | null;
}


export class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    // Fires during render → the tree unmounts and we render the
    // fallback. Returning a non-null state is what promotes us
    // from "reconciler crashed" to "boundary caught it."
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // Always console — a real dev opening DevTools can jump right
    // to the stack without waiting for the network POST.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", err, info.componentStack);

    // Best-effort remote log. The route may not exist yet on
    // older backends; catch and swallow so a logging failure
    // doesn't infinite-loop the boundary.
    //
    // Do NOT include `window.location.hash` (may contain OTP
    // fragments in some flows) or `document.cookie`. Path +
    // stack + message are enough to triage.
    try {
      void fetch("/api/v1/log/client-error", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: err.message,
          name: err.name,
          stack: err.stack?.slice(0, 4000) ?? null,
          component_stack: info.componentStack?.slice(0, 4000) ?? null,
          path: window.location.pathname,
          ua: navigator.userAgent.slice(0, 500),
        }),
        // Fire-and-forget: don't retry, don't wait, don't cancel
        // on unmount. Backend may 404 — that's fine.
        keepalive: true,
      }).catch(() => { /* swallow */ });
    } catch {
      /* nothing to do — the log endpoint doesn't exist yet */
    }
  }

  private handleReload = () => {
    // Full reload. `router.reload()` would try to remount inside
    // the same crashed tree; a real reload gives React a clean
    // slate AND picks up any newer bundle that shipped since the
    // tab was opened.
    window.location.reload();
  };

  private handleGoHome = () => {
    // Same-origin nav out of the broken route. The boundary's
    // fallback is INSIDE the tree, so this replaces the URL and
    // reloads — same recovery, different intent (user thinks
    // "get me out of here" rather than "try again").
    window.location.href = "/";
  };

  render(): ReactNode {
    if (this.state.err) {
      return (
        <div className={styles.wrap}>
          <div className={styles.card}>
            <h1 className={styles.title}>Something broke.</h1>
            <p className={styles.body}>
              AsterCash hit an unexpected error on this page. Nothing
              was submitted. Reload the page to try again, or return
              home.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primary}
                onClick={this.handleReload}
              >
                Reload
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={this.handleGoHome}
              >
                Go home
              </button>
            </div>
            {/* Details visible only in dev — production users don't
                need to see the stack. import.meta.env.DEV is compiled
                to `false` in `vite build` so the whole block is
                terser-eliminated in the production bundle. */}
            {import.meta.env.DEV && (
              <pre className={styles.stack}>{this.state.err.stack}</pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
